/**
 * AgentShield API Client
 *
 * Lightweight client for calling the AgentShield enforce API from middleware.
 * Designed for Edge Runtime compatibility (no Node.js-specific APIs).
 */

// ============================================================================
// Types
// ============================================================================

/**
 * API client configuration
 */
export interface AgentShieldClientConfig {
  /** API key for authentication */
  apiKey: string;
  /** API base URL (defaults to production) */
  baseUrl?: string;
  /**
   * Use edge detection for lower latency (~30-50ms vs ~150ms) and better coverage.
   * Edge detection can identify non-JS clients (curl, Python, Claude Code WebFetch)
   * that the pixel cannot detect since they don't execute JavaScript.
   * @default true
   */
  useEdge?: boolean;
  /** Request timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Enforcement action
 */
export type EnforcementAction = 'allow' | 'block' | 'redirect' | 'challenge' | 'log';

/**
 * Enforcement decision from the API
 */
export interface EnforcementDecision {
  action: EnforcementAction;
  reason: string;
  isAgent: boolean;
  confidence: number;
  agentName?: string;
  agentType?: string;
  redirectUrl?: string;
  message?: string;
  metadata?: {
    policyVersion?: string;
    signatureVerified?: boolean;
    denyListMatch?: {
      clientDid?: string;
      agentDid?: string;
      clientName?: string;
      reason?: string;
    };
  };
}

/**
 * Detection result (optional in response)
 */
export interface DetectionResult {
  isAgent: boolean;
  confidence: number;
  agentName?: string;
  agentType?: string;
  /** Detection class: 'human', 'ai_agent', 'bot', 'incomplete_data' */
  detectionClass?: string;
  verificationMethod?: string;
  reasons?: string[];
  /** Detection engine used: 'wasm' or 'javascript-fallback' */
  detectionMethod?: string;
}

/**
 * Enforce API response
 */
export interface EnforceResponse {
  success: boolean;
  data?: {
    decision: EnforcementDecision;
    processingTimeMs: number;
    requestId: string;
    detection?: DetectionResult;
  };
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Request input for enforce API
 */
export interface EnforceInput {
  /** HTTP headers from the incoming request */
  headers?: Record<string, string>;
  /** User-Agent header */
  userAgent?: string;
  /** Client IP address */
  ipAddress?: string;
  /** Request path */
  path?: string;
  /** Request URL */
  url?: string;
  /** HTTP method */
  method?: string;
  /** Request ID for tracing */
  requestId?: string;
  /** Options */
  options?: {
    /** Include full detection result */
    includeDetectionResult?: boolean;
    /** Cache TTL override */
    cacheTTL?: number;
  };
}

/**
 * Input for logging a detection result
 */
export interface LogDetectionInput {
  /** Detection result from Gateway */
  detection: DetectionResult;
  /** Request context */
  context: {
    userAgent?: string;
    ipAddress?: string;
    path?: string;
    url?: string;
    method?: string;
  };
  /** Source of the detection */
  source?: 'gateway' | 'middleware';
}

// ============================================================================
// Client Implementation
// ============================================================================

const DEFAULT_BASE_URL = 'https://kya.vouched.id';
const EDGE_DETECT_URL = 'https://detect.checkpoint-gateway.ai';
const DEFAULT_TIMEOUT = 5000;

/**
 * AgentShield API Client
 *
 * @example
 * ```typescript
 * const client = new AgentShieldClient({
 *   apiKey: process.env.AGENTSHIELD_API_KEY!,
 * });
 *
 * const result = await client.enforce({
 *   headers: Object.fromEntries(request.headers),
 *   path: request.nextUrl.pathname,
 *   method: request.method,
 * });
 *
 * if (result.decision.action === 'block') {
 *   return new Response('Access denied', { status: 403 });
 * }
 * ```
 */
export class AgentShieldClient {
  private apiKey: string;
  private baseUrl: string;
  private useEdge: boolean;
  private timeout: number;
  private debug: boolean;

  constructor(config: AgentShieldClientConfig) {
    if (!config.apiKey) {
      throw new Error('AgentShield API key is required');
    }

    this.apiKey = config.apiKey;
    // Default to edge detection for better coverage (detects non-JS clients)
    this.useEdge = config.useEdge !== false; // true by default
    this.baseUrl = config.baseUrl || (this.useEdge ? EDGE_DETECT_URL : DEFAULT_BASE_URL);
    this.timeout = config.timeout || DEFAULT_TIMEOUT;
    this.debug = config.debug || false;
  }

  /**
   * Call the enforce API to check if a request should be allowed
   */
  async enforce(input: EnforceInput): Promise<EnforceResponse> {
    const startTime = Date.now();

    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        // Use edge endpoint or Vercel API based on configuration
        const endpoint = this.useEdge
          ? `${this.baseUrl}/__detect/enforce`
          : `${this.baseUrl}/api/v1/enforce`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.apiKey}`,
            'X-Request-ID': input.requestId || crypto.randomUUID(),
          },
          body: JSON.stringify(input),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Parse response
        const data = (await response.json()) as EnforceResponse;

        if (this.debug) {
          console.log('[AgentShield] Enforce response:', {
            status: response.status,
            action: data.data?.decision.action,
            processingTimeMs: Date.now() - startTime,
          });
        }

        // Handle non-2xx responses
        if (!response.ok) {
          return {
            success: false,
            error: {
              code: `HTTP_${response.status}`,
              message: data.error?.message || `HTTP error: ${response.status}`,
            },
          };
        }

        return data;
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      // Handle timeout
      if (error instanceof Error && error.name === 'AbortError') {
        if (this.debug) {
          console.warn('[AgentShield] Request timed out');
        }
        return {
          success: false,
          error: {
            code: 'TIMEOUT',
            message: `Request timed out after ${this.timeout}ms`,
          },
        };
      }

      // Handle network errors
      if (this.debug) {
        console.error('[AgentShield] Request failed:', error);
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network request failed',
        },
      };
    }
  }

  /**
   * Quick check - returns just the action without full response parsing
   * Useful for very fast middleware that just needs allow/block
   */
  async quickCheck(input: EnforceInput): Promise<{
    action: EnforcementAction;
    error?: string;
  }> {
    const result = await this.enforce(input);

    if (!result.success || !result.data) {
      // On error, default to allow (fail-open)
      return {
        action: 'allow',
        error: result.error?.message,
      };
    }

    return {
      action: result.data.decision.action,
    };
  }

  /**
   * Check if this client is using edge detection (Gateway Worker)
   */
  isUsingEdge(): boolean {
    return this.useEdge;
  }

  /**
   * Log a detection result to AgentShield database.
   * Use after Gateway Worker detection to persist results.
   * Fire-and-forget - returns immediately without waiting for DB write.
   *
   * @example
   * ```typescript
   * // After receiving Gateway response
   * if (client.isUsingEdge() && response.data?.detection) {
   *   client.logDetection({
   *     detection: response.data.detection,
   *     context: { userAgent, ipAddress, path, url, method }
   *   }).catch(err => console.error('Log failed:', err));
   * }
   * ```
   */
  async logDetection(input: LogDetectionInput): Promise<void> {
    // Don't await - fire and forget
    // Use the base URL (not edge) for logging since this goes to the main API
    const logEndpoint = this.useEdge
      ? `${DEFAULT_BASE_URL}/api/v1/log-detection`
      : `${this.baseUrl}/api/v1/log-detection`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        const response = await fetch(logEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.apiKey}`,
          },
          body: JSON.stringify({
            detection: {
              isAgent: input.detection.isAgent,
              confidence: input.detection.confidence,
              agentName: input.detection.agentName,
              agentType: input.detection.agentType,
              detectionClass: input.detection.detectionClass,
              verificationMethod: input.detection.verificationMethod,
              reasons: input.detection.reasons,
            },
            context: input.context,
            source: input.source || 'gateway',
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok && this.debug) {
          console.warn('[AgentShield] Log detection returned non-2xx:', response.status);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    } catch (error) {
      // Silently fail for fire-and-forget, but log in debug mode
      if (this.debug) {
        console.error('[AgentShield] Log detection failed:', error);
      }
      // Re-throw so caller can catch if needed
      throw error;
    }
  }
}

/**
 * Create a singleton client instance
 *
 * @example
 * ```typescript
 * // In middleware.ts
 * import { getAgentShieldClient } from '@kya-os/agentshield-nextjs';
 *
 * const client = getAgentShieldClient();
 * ```
 */
let clientInstance: AgentShieldClient | null = null;

export function getAgentShieldClient(config?: Partial<AgentShieldClientConfig>): AgentShieldClient {
  if (!clientInstance) {
    const apiKey = config?.apiKey || process.env.AGENTSHIELD_API_KEY;

    if (!apiKey) {
      throw new Error(
        'AgentShield API key is required. Set AGENTSHIELD_API_KEY environment variable or pass apiKey in config.'
      );
    }

    clientInstance = new AgentShieldClient({
      apiKey,
      baseUrl: config?.baseUrl || process.env.AGENTSHIELD_API_URL,
      // Default to edge detection unless explicitly disabled
      useEdge: config?.useEdge ?? process.env.AGENTSHIELD_USE_EDGE !== 'false',
      timeout: config?.timeout,
      debug: config?.debug || process.env.AGENTSHIELD_DEBUG === 'true',
    });
  }

  return clientInstance;
}

/**
 * Reset the singleton client (useful for testing)
 */
export function resetAgentShieldClient(): void {
  clientInstance = null;
}
